"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.usePlayableElement = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _react = require("react");
var _feature = require("@studio/feature.flags");
var _defaultVolume = require("../../constants/defaultVolume");
var _VideoPlayerHelper = require("../../helpers/VideoPlayerHelper");
var _VideoPlayerContext = require("../../contexts/VideoPlayerContext");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2["default"])(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var usePlayableElement = exports.usePlayableElement = function usePlayableElement(mediaElementRef, _ref, serverTime, serverTimeDelta, sourceId) {
  var _ref$isPlaying = _ref.isPlaying,
    isPlaying = _ref$isPlaying === void 0 ? false : _ref$isPlaying,
    _ref$loop = _ref.loop,
    loop = _ref$loop === void 0 ? false : _ref$loop,
    _ref$volume = _ref.volume,
    volume = _ref$volume === void 0 ? _defaultVolume.DEFAULT_VOLUME : _ref$volume,
    _ref$muted = _ref.muted,
    muted = _ref$muted === void 0 ? false : _ref$muted,
    _ref$seek = _ref.seek,
    seek = _ref$seek === void 0 ? 0 : _ref$seek,
    startedOn = _ref.startedOn;
  var _useState = (0, _react.useState)(undefined),
    _useState2 = (0, _slicedToArray2["default"])(_useState, 2),
    duration = _useState2[0],
    setDuration = _useState2[1];
  var isVideoJsPlayer = (0, _feature.shouldHaveFeature)('studio.videoJsPlayer');
  var videoPlayerContext = (0, _react.useContext)(_VideoPlayerContext.VideoPlayerContext);

  /**
   * Set the duration, but only once
   */
  var onDurationChange = function onDurationChange() {
    if (duration) {
      console.log("onDurationChange(): Duration already set to ".concat(duration));
      return;
    }
    console.log("onDurationChange(): Setting duration for mediaElementRef ".concat(mediaElementRef.current.duration));
    setDuration(mediaElementRef.current.duration);
  };
  var _useState3 = (0, _react.useState)(false),
    _useState4 = (0, _slicedToArray2["default"])(_useState3, 2),
    canPlay = _useState4[0],
    setCanPlay = _useState4[1];
  (0, _react.useEffect)(function () {
    var _mediaElementRef$curr;
    if (isVideoJsPlayer) return;
    if (mediaElementRef !== null && mediaElementRef !== void 0 && (_mediaElementRef$curr = mediaElementRef.current) !== null && _mediaElementRef$curr !== void 0 && _mediaElementRef$curr.duration) {
      setDuration(mediaElementRef.current.duration);
    }
  }, [mediaElementRef.current, isVideoJsPlayer]);
  (0, _react.useEffect)(function () {
    if (isVideoJsPlayer) return;
    var desiredCurrentTime = (0, _VideoPlayerHelper.getCurrentPlaybackPosition)(isPlaying, startedOn, seek, duration, serverTime, serverTimeDelta, loop);
    if (desiredCurrentTime !== undefined) mediaElementRef.current.currentTime = desiredCurrentTime;
    var mediaElement = mediaElementRef.current;
    if (canPlay && isPlaying && mediaElement.paused) {
      // video is "over" and has run out already, and should not play
      if (!loop && mediaElement.currentTime === duration) {
        return;
      }
      var playPromise = mediaElement.play();
      if (playPromise !== undefined) {
        playPromise["catch"](function (error) {
          console.log('OUTPUT error :: ', error);
        });
      }
    }
    if (!isPlaying && !mediaElement.paused) {
      mediaElement.pause();
    }
    var handleCanPlay = function handleCanPlay() {
      return setCanPlay(true);
    };
    var visibilityChangeHandler = function visibilityChangeHandler() {
      // override auto-pause for videos without audio when tab is inactive
      if (isPlaying && mediaElement.paused && document.visibilityState === 'hidden' && mediaElement.currentTime < mediaElement.duration) {
        mediaElement.play();
      }

      //if the tab was hidden, but is now visible, set the seek position to where it "should" be.
      //In cases where the media is muted, chrome or other browsers may stop playback if the tab is inactive, so when the tab becomes active
      //again, it is necessary to reset the seek position to where it should be based on the start time
      if (muted && document.visibilityState === 'visible') {
        var _desiredCurrentTime = (0, _VideoPlayerHelper.getCurrentPlaybackPosition)(isPlaying, startedOn, seek, duration, serverTime, serverTimeDelta, loop);
        if (_desiredCurrentTime !== undefined) mediaElementRef.current.currentTime = _desiredCurrentTime;
      }
    };
    var handleLoadedMetadata = function handleLoadedMetadata() {
      console.log("[Player] Player ready with ID: ".concat(sourceId));
      videoPlayerContext.setPlayersInitialized(function (prevState) {
        return _objectSpread(_objectSpread({}, prevState), {}, (0, _defineProperty2["default"])({}, sourceId, true));
      });
    };
    document.addEventListener('visibilitychange', visibilityChangeHandler);
    mediaElement.addEventListener('durationchange', onDurationChange);
    mediaElement.addEventListener('canplay', handleCanPlay);
    mediaElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    return function () {
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
      mediaElement.removeEventListener('durationchange', onDurationChange);
      mediaElement.removeEventListener('canplay', handleCanPlay);
      mediaElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [canPlay, isPlaying, muted, seek, startedOn, duration, serverTime, serverTimeDelta, isVideoJsPlayer]);
  (0, _react.useEffect)(function () {
    if (isVideoJsPlayer) return;
    //Added this unmount hook code to address this issue related to Chrome 92 update: https://bugs.chromium.org/p/chromium/issues/detail?id=1232649
    return function () {
      if (mediaElementRef.current) {
        console.log("[Player] Cleaning up playable media object! with ID: ".concat(sourceId));
        try {
          mediaElementRef.current.srcObject = null;
        } catch (e) {
          console.log('Failed to clean up playable media object!', e);
        }
      }
    };
  }, [mediaElementRef, isVideoJsPlayer]);
  (0, _react.useEffect)(function () {
    if (isVideoJsPlayer) return;
    mediaElementRef.current.loop = loop;
    mediaElementRef.current.volume = volume;
    mediaElementRef.current.muted = muted;
  }, [loop, volume, muted, isVideoJsPlayer]);
};